/**
 * Популярные шрифты Google Fonts для выбора в настройках приветствия (моб. редактор).
 * Полный каталог на https://fonts.google.com — любое имя можно добавить вручную.
 */
export const GOOGLE_FONTS_WELCOME_CATALOG = [
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Oswald',
  'Raleway',
  'PT Sans',
  'PT Serif',
  'Merriweather',
  'Nunito',
  'Rubik',
  'Ubuntu',
  'Playfair Display',
  'Inter',
  'Noto Sans',
  'Noto Serif',
  'Source Sans 3',
  'Source Serif 4',
  'Work Sans',
  'Manrope',
  'Fira Sans',
  'Mulish',
  'Barlow',
  'DM Sans',
  'Outfit',
  'Plus Jakarta Sans',
  'Sora',
  'Lexend',
  'Figtree',
  'Onest',
  'Jost',
  'Cabin',
  'Karla',
  'Libre Franklin',
  'Heebo',
  'Assistant',
  'Varela Round',
  'Signika',
  'Exo 2',
  'Comfortaa',
  'Quicksand',
  'Pacifico',
  'Dancing Script',
  'Lobster',
  'Bebas Neue',
  'Anton',
  'Russo One',
  'Press Start 2P',
  'IBM Plex Sans',
  'IBM Plex Serif',
  'Space Grotesk',
  'Syne',
  'Archivo',
  'Titillium Web',
  'Crimson Text',
  'Libre Baskerville',
  'Bitter',
  'Arvo',
  'Alegreya',
  'Cormorant Garamond',
  'Spectral',
  'Cardo',
  'Old Standard TT',
  'Amatic SC',
  'Caveat',
  'Great Vibes',
  'Satisfy',
  'Permanent Marker',
  'Architects Daughter',
  'Indie Flower',
  'Shadows Into Light',
  'Patrick Hand',
  'Marck Script',
  'Bad Script',
  'Underdog',
  'Stalinist One',
  'Rubik Mono One',
  'Scada',
  'Tenor Sans',
  'Philosopher',
  'Poiret One',
  'Anonymous Pro',
  'JetBrains Mono',
  'Fira Code',
  'Source Code Pro',
  'IBM Plex Mono',
  'Noto Sans Mono',
];

/**
 * Подключение CSS Google Fonts через наш backend (тот же origin), чтобы на проде работало
 * при строгом CSP / корпоративных ограничениях на fonts.googleapis.com и fonts.gstatic.com.
 */
export function googleFontCss2Href(familyName) {
  const t = String(familyName || '').trim();
  if (!t) return '';
  return `/api/news/fonts/css?family=${encodeURIComponent(t)}`;
}

/** Значение для CSS font-family / option value */
export function googleFontFamilyCssValue(familyName) {
  const t = String(familyName || '').trim();
  if (!t) return 'sans-serif';
  const escaped = t.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  return `'${escaped}', sans-serif`;
}

/** Имена в кавычках из одной или нескольких CSS font-family строк (для подгрузки Google Fonts). */
export function collectQuotedFontNamesFromFontStacks(...stacks) {
  const names = [];
  for (const stack of stacks) {
    if (!stack || typeof stack !== 'string') continue;
    const re = /'([^']*)'|"([^"]*)"/g;
    let m;
    while ((m = re.exec(stack)) !== null) {
      const n = (m[1] != null ? m[1] : m[2]).trim();
      if (n) names.push(n);
    }
  }
  return names;
}

const GENERIC_OR_SYSTEM_FONT = new Set(
  [
    'sans-serif',
    'serif',
    'monospace',
    'cursive',
    'fantasy',
    'system-ui',
    'ui-sans-serif',
    'ui-serif',
    'ui-monospace',
    'inherit',
    'initial',
    'unset',
    'default',
    'arial',
    'helvetica',
    'times new roman',
    'courier new',
    'georgia',
    'verdana',
    'trebuchet ms',
    'impact',
    'comic sans ms',
    'segoe ui',
    'tahoma',
    'apple color emoji',
    'segoe ui emoji',
    'segoe ui symbol',
    'noto color emoji',
  ].map((s) => s.toLowerCase())
);

/**
 * Имена семейств для подключения Google Fonts: кавычки + первое «реальное» имя в стеке
 * (если строка пришла без кавычек или БД слегка исказила значение).
 */
export function collectGoogleFontNamesFromStacks(...stacks) {
  const seen = new Set();
  const out = [];
  const push = (name) => {
    const t = String(name || '').trim().replace(/\s+/g, ' ');
    if (!t || t.length > 120) return;
    const key = t.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(t);
  };

  collectQuotedFontNamesFromFontStacks(...stacks).forEach(push);

  for (const stack of stacks) {
    if (!stack || typeof stack !== 'string') continue;
    const parts = stack.split(',');
    for (const part of parts) {
      let t = part.trim().replace(/^["']+|["']+$/g, '').replace(/\s+/g, ' ');
      if (!t) continue;
      const low = t.toLowerCase();
      if (GENERIC_OR_SYSTEM_FONT.has(low)) continue;
      if (/[;<>{}]/.test(t)) continue;
      push(t);
      break;
    }
  }
  return out;
}

/**
 * Добавляет/удаляет <link> на fonts.googleapis.com для каждого семейства.
 * @param {string[]} families
 * @param {{ preview?: boolean }} opts preview — атрибут для превью в модалке (снимается при размонтировании эффекта)
 */
export function syncWelcomeGoogleFontLinkElements(families, opts = {}) {
  const preview = Boolean(opts.preview);
  const attr = preview ? 'data-welcome-google-font-preview' : 'data-welcome-google-font-app';
  const list = Array.isArray(families)
    ? [...new Set(families.map((f) => String(f).trim()).filter(Boolean))]
    : [];
  const want = new Set(list);

  document.querySelectorAll(`link[${attr}]`).forEach((el) => {
    const fam = el.getAttribute(attr);
    if (!want.has(fam)) {
      el.remove();
    }
  });

  list.forEach((family) => {
    let existing = false;
    document.querySelectorAll(`link[${attr}]`).forEach((el) => {
      if (el.getAttribute(attr) === family) existing = true;
    });
    if (existing) return;
    const href = googleFontCss2Href(family);
    if (!href) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute(attr, family);
    document.head.appendChild(link);
  });
}

export function removeAllWelcomeGoogleFontPreviewLinks() {
  document.querySelectorAll('link[data-welcome-google-font-preview]').forEach((el) => el.remove());
}

/** <link> для Google Fonts в ленте новостей (отдельные атрибуты, чтобы не трогать приветствие). */
export function syncNewsGoogleFontLinkElements(families, opts = {}) {
  const preview = Boolean(opts.preview);
  const attr = preview ? 'data-news-google-font-preview' : 'data-news-google-font-app';
  const list = Array.isArray(families)
    ? [...new Set(families.map((f) => String(f).trim()).filter(Boolean))]
    : [];
  const want = new Set(list);

  document.querySelectorAll(`link[${attr}]`).forEach((el) => {
    const fam = el.getAttribute(attr);
    if (!want.has(fam)) el.remove();
  });

  list.forEach((family) => {
    let existing = false;
    document.querySelectorAll(`link[${attr}]`).forEach((el) => {
      if (el.getAttribute(attr) === family) existing = true;
    });
    if (existing) return;
    const href = googleFontCss2Href(family);
    if (!href) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute(attr, family);
    document.head.appendChild(link);
  });
}

export function removeAllNewsGoogleFontPreviewLinks() {
  document.querySelectorAll('link[data-news-google-font-preview]').forEach((el) => el.remove());
}

/** Кастомные CSS-URL шрифтов (как в настройках приветствия). */
export function syncNewsCustomCssLinks(urls, opts = {}) {
  const preview = Boolean(opts.preview);
  const attr = preview ? 'data-news-custom-css-preview' : 'data-news-custom-css-app';
  const list = Array.isArray(urls)
    ? [...new Set(urls.map((u) => String(u).trim()).filter(Boolean))]
    : [];
  const want = new Set(list);

  document.querySelectorAll(`link[${attr}]`).forEach((el) => {
    const u = el.getAttribute(attr);
    if (!want.has(u)) el.remove();
  });

  list.forEach((url) => {
    let existing = false;
    document.querySelectorAll(`link[${attr}]`).forEach((el) => {
      if (el.getAttribute(attr) === url) existing = true;
    });
    if (existing) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.setAttribute(attr, url);
    document.head.appendChild(link);
  });
}

export function removeAllNewsCustomCssPreviewLinks() {
  document.querySelectorAll('link[data-news-custom-css-preview]').forEach((el) => el.remove());
}
